import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GAService } from '@domgen/dgx-fe-ga';
import { AnalyticsService } from '@domgen/dgx-components';
import {
  getProductName,
  PlanDetailViewModel,
} from '../types/plan.detail.model';
import { User } from '../types/user.model';

@Injectable({
  providedIn: 'root',
})
export class PlanDetailsTrackingService {
  constructor(
    private analyticsService: AnalyticsService,
    private gaService: GAService
  ) {}

  trackCancelRepair(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'view-my-policy-single-plan',
      eventAction: 'plan_details',
      eventCategory: 'current_booking',
      eventLabel: 'attempt_cancel_booking',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
    });
  }

  trackCancelPlan(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'plan_info',
      event_label: 'cancel_plan',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      cancelPlan: '1',
      emailPlan: '1',
    });
  }

  trackCancelService(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'genericGAEvent',
      pagename: 'view-my-policy-single-plan',
      eventAction: 'plan_details',
      eventCategory: 'current_asv',
      eventLabel: 'attempt_cancel_asv',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
    });
  }

  trackEmailPlan(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'plan_info',
      event_label: 'email_plan',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      cancelPlan: '1',
      emailPlan: '1',
    });
  }

  trackEmailPlanError(planDetailViewModel: PlanDetailViewModel, user: User) {
    this.analyticsService.push({
      pagename: 'call-cs-email-plan',
      genericpagename: 'myplan-call-cs',
      category: 'myplan-call-cs',
      event: 'laEvent',
      laEvent: 'call-cs',
      customerID: user.sub ? user.sub : '',
      DigitalID: user['custom:digital_id'] ? user['custom:digital_id'] : '',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      productsStatus: planDetailViewModel.state.toLowerCase(),
      productModel: planDetailViewModel.item.model_number || '',
    });
  }

  trackCancelRepairError(
    planDetailViewModel: PlanDetailViewModel,
    user: User,
    errorData: HttpErrorResponse
  ) {
    this.analyticsService.push({
      event: 'spa-pageview',
      pagename: 'call-cs-cancel-booking',
      category: 'myplan-call-cs',
      customerID: user.sub ? user.sub : '',
      DigitalID: user['custom:digital_id'] ? user['custom:digital_id'] : '',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      productsStatus: planDetailViewModel.state.toLowerCase(),
      productModel: planDetailViewModel.item.model_number || '',
    });

    this.analyticsService.push({
      pagename: 'call-cs-cancel-booking',
      event: 'genericGAEvent',
      eventCategory: 'rebooking_errors',
      eventAction: errorData.status, // dynamically populates the error code
      eventLabel: errorData.error.message,
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
    });
  }

  trackCancelServiceError(
    planDetailViewModel: PlanDetailViewModel,
    user: User
  ) {
    this.analyticsService.push({
      pagename: 'call-cs-cancel-asv',
      genericpagename: 'myplan-call-cs',
      category: 'myplan-call-cs',
      event: 'laEvent',
      laEvent: 'call-asv',
      customerID: user.sub ? user.sub : '',
      DigitalID: user['custom:digital_id'] ? user['custom:digital_id'] : '',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      productsStatus: planDetailViewModel.state.toLowerCase(),
      productModel: planDetailViewModel.item.model_number || '',
    });
  }

  trackDocumentDownloadError(error: string) {
    this.analyticsService.push({
      clientName: 'DG My Account',
      event: 'genericGAEvent',
      eventCategory: 'plan_details',
      eventAction: 'plan-download-error',
      eventLabel: error,
    });
  }

  trackRepairerPhoneNumberClick(planDetailViewModel: PlanDetailViewModel) {
    const pageName = planDetailViewModel.multiPlanNumber
      ? 'view-my-policy-multiplan'
      : 'view-my-policy-single-plan';

    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'phone-number',
      eventCategory: 'phone-number-click',
      eventAction: pageName,
      eventLabel: planDetailViewModel.repairerDetails.phone,
    });
  }

  trackMaintenanceSupportClick() {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'appliance-care',
      eventCategory: 'plan_details',
      eventAction: 'appliance-care',
      eventLabel: 'maintenance',
    });
  }

  trackDocumentDownloadClick() {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'plan_info',
      event_label: 'download_plan',
    });
  }

  trackDocumentDownloadTooltipClick() {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'plan_info',
      event_label: 'tooltip',
    });
  }

  trackHelpAndFaqClick(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'plan_info',
      event_label: 'help_and_faqs',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      cancelPlan: '1',
      emailPlan: '1',
    });
  }

  trackHelpAndFaqInfoClick() {
    this.analyticsService.push({
      clientName: 'DG My Account',
      event: 'genericGAEvent',
      eventCategory: 'plan_details',
      eventAction: 'plan_info',
      eventLabel: 'my_account_FAQ',
    });
  }

  trackHelpAndFaqModalClick(): void {
    this.gaService.tag({
      clientName: 'DG My Account',
      event: 'genericGAEvent',
      eventCategory: 'plan_details',
      eventAction: 'plan_info',
      eventLabel: 'plan_doc_FAQ',
    });
  }

  trackModalCloseClick(): void {
    this.gaService.tag({
      clientName: 'DG My Account',
      event: 'genericGAEvent',
      eventCategory: 'plan_details',
      eventAction: 'plan_info',
      eventLabel: 'plan_doc_error_modal_close',
    });
  }

  trackDirectDebitChange(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'payment',
      event_label: 'how_to_change_direct_debit',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      changePayment: '1',
    });
  }

  trackPageView(planDetailViewModel: PlanDetailViewModel, user: User) {
    if (
      planDetailViewModel.state === 'CANCELLED' ||
      planDetailViewModel.state === 'LAPSED'
    )
      return;

    if (planDetailViewModel?.item?.code === 'X7') {
      this.analyticsService.push({
        event: 'spa-pageview',
        pagename: 'view-my-policy-single-plan-plumbing',
        genericpagename: 'view-my-policy-plumbing',
        category: 'plumbing-and-drainage',
        planNumber: planDetailViewModel.number,
        productName: getProductName(planDetailViewModel),
        customerID: user.sub ? user.sub : '',
        DigitalID: user['custom:digital_id'] ? user['custom:digital_id'] : '',
      });
    }

    const isMultiPlan = !!planDetailViewModel.multiPlanNumber;
    let pageName = '';

    if (planDetailViewModel.breakdownCoverStartDate) {
      pageName = isMultiPlan
        ? 'view-my-extended-FT-policy-multiplan'
        : 'view-my-extended-FT-policy-single-plan';
    } else {
      pageName = isMultiPlan
        ? 'view-my-policy-multiplan'
        : 'view-my-policy-single-plan';
    }

    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view-plans',
      customerID: user.sub ? user.sub : '',
      DigitalID: user['custom:digital_id'] ? user['custom:digital_id'] : '',
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      productsStatus: planDetailViewModel.state.toLowerCase(),
      productModel: planDetailViewModel.item.model_number || '',
      pagename: pageName,
      genericpagename: 'view-my-policy',
      category: `app_sc_${(planDetailViewModel.item.code_description || '')
        .replace(/\s/g, '_')
        .toLowerCase()}`,
      excessClaimsMessage: planDetailViewModel?.repairPaymentType
        ? `${planDetailViewModel?.repairPaymentType}`
        : 'NO',
    });
  }

  trackBreakdownCoverClick(): void {
    this.gaService.tag({
      event: 'genericGAEvent',
      clientName: 'DG My Account',
      eventCategory: 'plan_details',
      eventAction: 'button',
      eventLabel: 'breakdown_cover',
    });
  }

  trackRepairCtaClickEvent(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'button',
      event_label: planDetailViewModel.repairAction.toLowerCase(),
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      startRepair: '1',
    });
  }

  trackServiceCtaClickEvent(planDetailViewModel: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'laEvent',
      laEvent: 'view_my_plans',
      event_category: 'plan_details',
      event_action: 'button',
      event_label: planDetailViewModel.serviceAction.toLowerCase(),
      planNumber: planDetailViewModel.number,
      productName: getProductName(planDetailViewModel),
      startRepair: '1',
    });
  }

  trackOptOutAutoRenew(planDetails: PlanDetailViewModel) {
    this.analyticsService.push({
      event: 'genericGAEvent',
      eventAction: 'interaction',
      eventCategory: planDetails.multiPlanNumber
        ? 'auto-renewal-multi-plan'
        : 'auto-renewal-single-plan',
      eventLabel: 'Start or stop my policy being automatically renewed',
      clientName: 'DG My Account',
    });
  }

  trackManageRepairsClick() {
    this.analyticsService.push({
      clientName: 'DG My Account',
      event: 'genericGAEvent',
      eventcategory: 'plan_details',
      eventaction: 'button',
      eventlabel: 'request_repair',
    });
  }

  trackPhoneNumberClicked(planDetails: PlanDetailViewModel) {
    const pageName = planDetails.multiPlanNumber
      ? 'view-my-policy-multiplan'
      : 'view-my-policy-single-plan';

    this.analyticsService.push({
      event: 'genericGAEvent',
      laEvent: 'phone-number',
      eventCategory: 'phone-number-click',
      eventAction: pageName,
      eventLabel: '0333 000 4966',
    });
  }

  trackDDModalClose() {
    this.analyticsService.push({
      event: 'genericGAEvent',
      eventCategory: 'plan_details',
      eventAction: 'plan_info',
      eventLabel: 'DD_modal_close',
    });
  }
}
